<template>
  <div style="max-width: 90%; margin: auto" class="py-10">
    <!-- Header -->
    <div class="d-flex justify-space-between align-center mb-8 flex-wrap" style="gap: 16px">
      <div>
        <p class="page-subtitle mb-1">Pipeline</p>
        <h1 class="page-title">Reviews</h1>
      </div>
      <button class="icon-btn icon-btn-outlined" :disabled="anyLoading" @click="refreshCurrentTab" title="Aktualisieren">
        <v-progress-circular v-if="anyLoading" indeterminate size="14" width="2" color="#0a1628" />
        <v-icon v-else size="16">mdi-refresh</v-icon>
      </button>
    </div>

    <!-- Tabs -->
    <div class="tab-bar mb-6">
      <button class="tab" :class="{ 'tab-active': activeTab === 'form' }" @click="switchTab('form')">
        <v-icon size="16">mdi-plus-circle-outline</v-icon>
        Review einreichen
      </button>
      <button class="tab" :class="{ 'tab-active': activeTab === 'mine' }" @click="switchTab('mine')">
        <v-icon size="16">mdi-clipboard-list-outline</v-icon>
        Meine Reviews
      </button>
      <div style="flex: 1"></div>
      <button
        v-if="$user && $user.isTechnicalReviewer"
        class="tab"
        :class="{ 'tab-active': activeTab === 'technical' }"
        @click="switchTab('technical')"
      >
        <v-icon size="16">mdi-code-tags</v-icon>
        Technisch
        <span v-if="technicalPendingCount > 0" class="tab-count tab-count-warning">{{ technicalPendingCount }}</span>
      </button>
      <button
        v-if="$user && $user.isVisualReviewer"
        class="tab"
        :class="{ 'tab-active': activeTab === 'visual' }"
        @click="switchTab('visual')"
      >
        <v-icon size="16">mdi-eye-outline</v-icon>
        Optisch
        <span v-if="visualPendingCount > 0" class="tab-count tab-count-warning">{{ visualPendingCount }}</span>
      </button>
    </div>

    <!-- ===================== TAB: FORMULAR ===================== -->
    <div v-if="activeTab === 'form'" class="card-modern">
      <h2 class="section-title mb-5">Neue Review-Anfrage</h2>

      <!-- Shop-Combobox -->
      <div class="fg mb-4" ref="shopDropdown">
        <label class="fl">Um welchen Onlineshop geht es? *</label>
        <div class="combo-wrap">
          <div class="iw" :class="{ 'iw-f': focusField === 'shop' }">
            <v-icon size="15" color="rgba(0,0,0,0.3)">mdi-store-outline</v-icon>
            <input
              v-model="shopSearch"
              class="ii"
              placeholder="Shop suchen oder eingeben…"
              @focus="onShopFocus"
              @blur="focusField = null"
              @input="shopMenuOpen = true"
            />
            <v-icon v-if="form.shopDomain" size="14" color="#aaa" style="cursor: pointer" @click="clearShop">mdi-close-circle</v-icon>
          </div>
          <div v-if="shopMenuOpen" class="combo-menu">
            <div v-if="loadingShops" class="combo-empty">Lade Shops…</div>
            <template v-else>
              <button
                v-for="s in filteredShops"
                :key="s.name"
                type="button"
                class="combo-option"
                @mousedown.prevent="selectShop(s)"
              >
                <v-icon size="14" color="#888">mdi-store-outline</v-icon>
                <span class="combo-label">{{ s.name }}</span>
              </button>
              <button
                v-if="showCreateShop"
                type="button"
                class="combo-option combo-option-create"
                @mousedown.prevent="createShop"
              >
                <v-icon size="14" color="#5B5BD6">mdi-plus-circle-outline</v-icon>
                <span class="combo-label">„{{ shopSearch }}“ als neuen Shop anlegen</span>
              </button>
              <div v-if="filteredShops.length === 0 && !showCreateShop" class="combo-empty">Keine Treffer</div>
            </template>
          </div>
        </div>
      </div>

      <!-- ABLyft Select -->
      <div class="fg mb-4" ref="ablyftDropdown">
        <label class="fl">ABLyft Test auswählen *</label>
        <button
          type="button"
          class="iw iw-trigger"
          :class="{ 'iw-f': ablyftMenuOpen, 'iw-disabled': !form.shopDomain || !selectedShopProjectId }"
          :disabled="!form.shopDomain || !selectedShopProjectId"
          @click="ablyftMenuOpen = !ablyftMenuOpen"
        >
          <v-icon size="15" color="rgba(0,0,0,0.3)">mdi-flask-outline</v-icon>
          <span v-if="form.ablyftExperiment" class="ii-val">{{ form.ablyftExperiment.name }}</span>
          <span v-else class="ii-placeholder">
            {{ !form.shopDomain ? 'Zuerst Onlineshop auswählen…' : 'Test im Status Preview auswählen…' }}
          </span>
          <v-progress-circular v-if="loadingExperiments" indeterminate size="14" width="2" color="#888" />
          <v-icon v-else size="14" color="#888" :class="{ 'chev-open': ablyftMenuOpen }">mdi-chevron-down</v-icon>
        </button>
        <div v-if="ablyftMenuOpen" class="combo-menu">
          <div v-if="loadingExperiments" class="combo-empty">Lade Tests…</div>
          <template v-else-if="ablyftExperiments.length > 0">
            <button
              v-for="exp in ablyftExperiments"
              :key="exp.id"
              type="button"
              class="combo-option"
              @click="selectAblyft(exp)"
            >
              <v-icon size="14" color="#5B5BD6">mdi-flask-outline</v-icon>
              <span class="combo-label">{{ exp.name }}</span>
              <v-icon v-if="form.ablyftExperiment?.id === exp.id" size="14" color="#2E7D32" style="margin-left: auto">mdi-check</v-icon>
            </button>
          </template>
          <div v-else class="combo-empty">
            {{ !selectedShopProjectId ? 'Diesem Shop ist kein ABLyft-Projekt zugeordnet.' : 'Keine Tests im Status "Preview" vorhanden.' }}
          </div>
        </div>
        <div v-if="form.ablyftExperiment?.url" class="ablyft-link">
          <a :href="form.ablyftExperiment.url" target="_blank">
            <v-icon size="12">mdi-flask-outline</v-icon>
            Test in ABLyft öffnen
            <v-icon size="10">mdi-open-in-new</v-icon>
          </a>
        </div>
      </div>

      <!-- Seite -->
      <div class="fg mb-4">
        <label class="fl">Auf welcher Seite wird getestet? *</label>
        <div class="iw" :class="{ 'iw-f': focusField === 'testPage' }">
          <v-icon size="15" color="rgba(0,0,0,0.3)">mdi-file-outline</v-icon>
          <input v-model="form.testPage" class="ii" placeholder="z.B. Startseite, PDP, Kategorie" @focus="focusField = 'testPage'" @blur="focusField = null" />
        </div>
      </div>

      <!-- Device Toggle -->
      <div class="fg mb-4">
        <label class="fl">Auf welchem Endgerät wird ausgestrahlt? *</label>
        <div class="device-toggle">
          <button class="device-btn" :class="{ 'device-active': form.device === 'Mobile' }" @click="form.device = 'Mobile'">
            <v-icon size="15">mdi-cellphone</v-icon>Mobile
          </button>
          <button class="device-btn" :class="{ 'device-active': form.device === 'Desktop' }" @click="form.device = 'Desktop'">
            <v-icon size="15">mdi-monitor</v-icon>Desktop
          </button>
          <button class="device-btn" :class="{ 'device-active': form.device === 'Mobile & Desktop' }" @click="form.device = 'Mobile & Desktop'">
            <v-icon size="15">mdi-cellphone-link</v-icon>Beide
          </button>
        </div>
      </div>

      <!-- Screenshots -->
      <div class="fg mb-4">
        <label class="fl">Screenshot der Section <span class="fl-hint">(max. 3)</span></label>
        <div class="dropzone" @click="$refs.screenshotInput.click()" @dragover.prevent @drop.prevent="onScreenshotDrop">
          <v-icon size="28" color="#c5cbd3" class="mb-2">mdi-image-plus</v-icon>
          <div class="dz-title">Klicken oder Dateien hierher ziehen</div>
          <div class="dz-sub">PNG, JPG, WEBP, AVIF, GIF, HEIC – bis 100 MB</div>
        </div>
        <input
          ref="screenshotInput"
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/webp,image/avif,image/gif,image/heic,image/heif"
          multiple style="display: none"
          @change="onScreenshotSelected"
        />
        <div v-if="screenshotPreviews.length > 0" class="screenshot-previews mt-3">
          <div v-for="(src, i) in screenshotPreviews" :key="i" class="preview-thumb">
            <v-img :src="src" aspect-ratio="1.5" cover />
            <button class="preview-remove" @click.stop="removeScreenshot(i)"><v-icon size="12">mdi-close</v-icon></button>
          </div>
        </div>
      </div>

      <!-- ABLyft bestätigt -->
      <div class="fg mb-4">
        <label class="fl">Wurde der Test in ABLyft angelegt und geprüft? *</label>
        <div class="radio-option" :class="{ 'radio-active': form.ablyftChecked === 'yes' }" @click="form.ablyftChecked = 'yes'">
          <div class="radio-dot" :class="{ 'radio-dot-on': form.ablyftChecked === 'yes' }"></div>
          <span class="radio-label">Ja, ist angelegt und überprüft.</span>
        </div>
        <div class="radio-option mt-2" :class="{ 'radio-active': form.ablyftChecked === 'no' }" @click="form.ablyftChecked = 'no'">
          <div class="radio-dot" :class="{ 'radio-dot-on': form.ablyftChecked === 'no' }"></div>
          <span class="radio-label">Nein, ist noch nicht angelegt.</span>
        </div>
      </div>

      <!-- Weitere Infos -->
      <div class="fg mb-4">
        <label class="fl">Weitere Informationen <span class="fl-hint">(optional)</span></label>
        <div class="iw iw-ta" :class="{ 'iw-f': focusField === 'additionalInfo' }">
          <textarea
            v-model="form.additionalInfo"
            class="ii ii-ta"
            rows="4"
            placeholder="Zusätzliche Hinweise, Besonderheiten, …"
            @focus="focusField = 'additionalInfo'"
            @blur="focusField = null"
          ></textarea>
        </div>
      </div>

      <!-- Deadline -->
      <div class="fg mb-4" style="max-width: 300px">
        <label class="fl">Deadline <span class="fl-hint">(optional)</span></label>
        <div class="iw" :class="{ 'iw-f': focusField === 'deadline' }">
          <v-icon size="15" color="rgba(0,0,0,0.3)">mdi-calendar-outline</v-icon>
          <input v-model="form.deadline" class="ii" placeholder="TT.MM.JJJJ" @focus="focusField = 'deadline'" @blur="focusField = null" />
        </div>
      </div>

      <div v-if="submitMessage" class="mt-4" :class="submitSuccess ? 'alert-success' : 'alert-error'">
        <v-icon size="16" :color="submitSuccess ? '#2E7D32' : '#D32F2F'">
          {{ submitSuccess ? 'mdi-check-circle-outline' : 'mdi-alert-circle-outline' }}
        </v-icon>
        {{ submitMessage }}
      </div>

      <button class="submit-btn mt-6" :disabled="submitting" @click="submitReview">
        <v-progress-circular v-if="submitting" indeterminate size="14" width="2" color="white" />
        <v-icon v-else size="16">mdi-send-outline</v-icon>
        <span>Review einreichen</span>
      </button>
    </div>

    <!-- ===================== TAB: MEINE REVIEWS ===================== -->
    <template v-else-if="activeTab === 'mine'">
      <div class="sub-toolbar mb-6">
        <div class="seg-toggle">
          <button class="seg-btn" :class="{ 'seg-active': mineView === 'active' }" @click="mineView = 'active'">
            <v-icon size="14">mdi-inbox-outline</v-icon>Aktiv
            <span v-if="activeMineCount > 0" class="seg-count">{{ activeMineCount }}</span>
          </button>
          <button class="seg-btn" :class="{ 'seg-active': mineView === 'archive' }" @click="mineView = 'archive'">
            <v-icon size="14">mdi-archive-outline</v-icon>Archiv
            <span v-if="archivedMineCount > 0" class="seg-count">{{ archivedMineCount }}</span>
          </button>
        </div>

        <div class="combo-wrap" ref="statusFilterDropdown" style="max-width: 240px; flex: 1">
          <button
            type="button"
            class="iw iw-trigger"
            :class="{ 'iw-f': statusFilterMenuOpen }"
            @click="statusFilterMenuOpen = !statusFilterMenuOpen"
          >
            <v-icon size="15" color="rgba(0,0,0,0.3)">mdi-filter-outline</v-icon>
            <span class="ii-val">{{ currentMineFilterLabel }}</span>
            <v-icon size="14" color="#888" :class="{ 'chev-open': statusFilterMenuOpen }">mdi-chevron-down</v-icon>
          </button>
          <div v-if="statusFilterMenuOpen" class="combo-menu">
            <button
              v-for="it in mineStatusFilterItems"
              :key="it.value"
              type="button"
              class="combo-option"
              @click="selectMineFilter(it.value)"
            >
              <span class="combo-label">{{ it.label }}</span>
              <v-icon v-if="mineFilterStatus === it.value" size="14" color="#2E7D32" style="margin-left: auto">mdi-check</v-icon>
            </button>
          </div>
        </div>
      </div>

      <ReviewsList
        :reviews="filteredMineReviews"
        :loading="loadingMyReviews"
        :current-user="$user"
        context="mine"
        :empty-title="mineView === 'archive' ? 'Archiv ist leer' : 'Keine Reviews vorhanden'"
        :empty-hint="mineView === 'archive' ? 'Abgeschlossene Reviews kannst du hier archivieren, um die Übersicht sauber zu halten.' : 'Du hast noch keine Reviews eingereicht.'"
        empty-icon="mdi-clipboard-list-outline"
        :resubmitting-id="resubmittingId"
        :archiving-id="archivingId"
        @resubmit="resubmitReview"
        @archive="archiveReview"
        @unarchive="unarchiveReview"
        @open-screenshot="openScreenshot"
      />
    </template>

    <!-- ===================== TAB: TECHNISCH ===================== -->
    <template v-else-if="activeTab === 'technical' && $user && $user.isTechnicalReviewer">
      <div class="sub-toolbar mb-6">
        <div class="seg-toggle">
          <button class="seg-btn" :class="{ 'seg-active': technicalBucket === 'pending' }" @click="technicalBucket = 'pending'">
            <v-icon size="14">mdi-inbox-outline</v-icon>Eingehend
            <span v-if="technicalPendingCount > 0" class="seg-count seg-count-warning">{{ technicalPendingCount }}</span>
          </button>
          <button class="seg-btn" :class="{ 'seg-active': technicalBucket === 'rejected' }" @click="technicalBucket = 'rejected'">
            <v-icon size="14">mdi-close-circle-outline</v-icon>Abgelehnt
          </button>
          <button class="seg-btn" :class="{ 'seg-active': technicalBucket === 'approved' }" @click="technicalBucket = 'approved'">
            <v-icon size="14">mdi-arrow-right-thin-circle-outline</v-icon>Durchgereicht
          </button>
        </div>
      </div>

      <ReviewsList
        :reviews="technicalReviewsForBucket"
        :loading="loadingTechnical"
        :current-user="$user"
        context="technical"
        :empty-title="technicalEmptyTitle"
        :empty-hint="technicalBucket === 'pending' ? 'Aktuell keine eingehenden technischen Reviews. Eigene Reviews werden hier nicht angezeigt und gehen automatisch an andere technische Reviewer.' : ''"
        :empty-icon="technicalBucket === 'pending' ? 'mdi-check-all' : 'mdi-folder-open-outline'"
        :accepting-id="acceptingId"
        @accept="acceptReview"
        @reject="openRejectDialog"
        @open-screenshot="openScreenshot"
      />
    </template>

    <!-- ===================== TAB: OPTISCH ===================== -->
    <template v-else-if="activeTab === 'visual' && $user && $user.isVisualReviewer">
      <div class="sub-toolbar mb-6">
        <div class="seg-toggle">
          <button class="seg-btn" :class="{ 'seg-active': visualBucket === 'pending' }" @click="visualBucket = 'pending'">
            <v-icon size="14">mdi-inbox-outline</v-icon>Eingehend
            <span v-if="visualPendingCount > 0" class="seg-count seg-count-warning">{{ visualPendingCount }}</span>
          </button>
          <button class="seg-btn" :class="{ 'seg-active': visualBucket === 'rejected' }" @click="visualBucket = 'rejected'">
            <v-icon size="14">mdi-close-circle-outline</v-icon>Abgelehnt
          </button>
          <button class="seg-btn" :class="{ 'seg-active': visualBucket === 'approved' }" @click="visualBucket = 'approved'">
            <v-icon size="14">mdi-check-all</v-icon>Approved
          </button>
        </div>
      </div>

      <ReviewsList
        :reviews="visualReviewsForBucket"
        :loading="loadingVisual"
        :current-user="$user"
        context="visual"
        :empty-title="visualEmptyTitle"
        :empty-hint="visualBucket === 'pending' ? 'Aktuell keine eingehenden optischen Reviews. Eigene Reviews werden hier nicht angezeigt und gehen automatisch an andere optische Reviewer.' : ''"
        :empty-icon="visualBucket === 'pending' ? 'mdi-check-all' : 'mdi-folder-open-outline'"
        :accepting-id="acceptingId"
        @accept="acceptReview"
        @reject="openRejectDialog"
        @open-screenshot="openScreenshot"
      />
    </template>

    <!-- DIALOG: Ablehnen -->
    <div v-if="rejectDialog" class="dialog-backdrop" @click="!rejecting && (rejectDialog = false)"></div>
    <v-dialog v-model="rejectDialog" max-width="520" :scrim="false" persistent>
      <v-card rounded="xl" class="modern-dialog" elevation="0">
        <button class="close-btn" :disabled="rejecting" @click="rejectDialog = false"><v-icon size="18">mdi-close</v-icon></button>
        <div class="dh">
          <div class="dh-icon" style="background: rgba(211,47,47,0.1)"><v-icon size="22" color="#D32F2F">mdi-close-circle-outline</v-icon></div>
          <div>
            <div class="dh-title">{{ rejectDialogTitle }}</div>
            <div class="dh-sub">{{ rejectDialogHint }}</div>
          </div>
        </div>
        <div class="db">
          <div class="fg mb-3">
            <label class="fl">Ablehnungsgrund *</label>
            <div class="iw iw-ta" :class="{ 'iw-f': focusField === 'reason' }">
              <textarea
                v-model="rejectForm.reason"
                class="ii ii-ta"
                rows="4"
                placeholder="Was muss noch angepasst oder ergänzt werden?"
                @focus="focusField = 'reason'"
                @blur="focusField = null"
              ></textarea>
            </div>
          </div>

          <div class="fg">
            <label class="fl">Screenshot <span class="fl-hint">(optional)</span></label>
            <button class="dropzone-small" @click="$refs.rejectScreenshotInput.click()">
              <v-icon size="16" color="#888">mdi-image-plus</v-icon>
              <span>Screenshot hinzufügen</span>
            </button>
            <input
              ref="rejectScreenshotInput"
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp,image/avif,image/gif,image/heic,image/heif"
              style="display: none"
              @change="onRejectScreenshotSelected"
            />
            <div v-if="rejectForm.screenshotPreview" class="preview-thumb mt-2" style="max-width: 200px">
              <v-img :src="rejectForm.screenshotPreview" aspect-ratio="1.5" cover />
            </div>
          </div>

          <div v-if="rejectMessage" class="alert-error mt-3">
            <v-icon size="16" color="#D32F2F">mdi-alert-circle-outline</v-icon>{{ rejectMessage }}
          </div>
        </div>
        <div class="dff">
          <button class="bo" :disabled="rejecting" @click="rejectDialog = false">Abbrechen</button>
          <button class="bp bp-danger" :disabled="rejecting" @click="submitReject">
            <v-progress-circular v-if="rejecting" indeterminate size="14" width="2" color="white" />
            <v-icon v-else size="15">mdi-close-circle-outline</v-icon>
            <span>Ablehnen</span>
          </button>
        </div>
      </v-card>
    </v-dialog>

    <!-- Lightbox -->
    <div v-if="lightboxDialog" class="dialog-backdrop" @click="lightboxDialog = false"></div>
    <v-dialog v-model="lightboxDialog" max-width="960" :scrim="false">
      <v-card rounded="xl" class="lightbox-card" elevation="0">
        <button class="lightbox-close" @click="lightboxDialog = false"><v-icon color="white">mdi-close</v-icon></button>
        <v-img :src="lightboxSrc" contain max-height="80vh" />
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import ReviewsList from './ReviewsList.vue';

export default {
  name: 'ReviewsOverview',
  components: { ReviewsList },

  data() {
    return {
      activeTab: new URLSearchParams(window.location.search).get('tab') || 'form',
      focusField: null,

      // Formular
      submitting: false,
      submitMessage: '',
      submitSuccess: false,
      shops: [],
      shopSearch: '',
      loadingShops: false,
      shopMenuOpen: false,
      ablyftMenuOpen: false,
      statusFilterMenuOpen: false,
      form: {
        shopDomain: null,
        ablyftExperiment: null,
        testPage: '',
        device: 'Mobile',
        ablyftChecked: null,
        additionalInfo: '',
        deadline: '',
      },
      ablyftExperiments: [],
      loadingExperiments: false,
      screenshotFiles: [],
      screenshotPreviews: [],

      // Meine Reviews
      mineView: 'active',
      mineFilterStatus: 'all',
      loadingMyReviews: false,
      myReviews: [],

      // Technisch
      technicalBucket: 'pending',
      loadingTechnical: false,
      technicalPendingReviews: [],
      technicalRejectedReviews: [],
      technicalApprovedReviews: [],

      // Optisch
      visualBucket: 'pending',
      loadingVisual: false,
      visualPendingReviews: [],
      visualRejectedReviews: [],
      visualApprovedReviews: [],

      // Actions
      resubmittingId: null,
      acceptingId: null,
      archivingId: null,

      // Reject-Dialog
      rejectDialog: false,
      rejectTarget: null,
      rejectForm: { reason: '', screenshotFile: null, screenshotPreview: null },
      rejecting: false,
      rejectMessage: '',

      // Lightbox
      lightboxDialog: false,
      lightboxSrc: null,
    };
  },

  computed: {
    anyLoading() { return this.loadingMyReviews || this.loadingTechnical || this.loadingVisual; },
    selectedShopProjectId() { return this.form.shopDomain?.ablyftProjectId || null; },

    filteredShops() {
      const q = (this.shopSearch || '').trim().toLowerCase();
      if (!q) return this.shops;
      return this.shops.filter((s) => s.name.toLowerCase().includes(q));
    },
    showCreateShop() {
      const q = (this.shopSearch || '').trim();
      if (!q) return false;
      return !this.shops.some((s) => s.name.toLowerCase() === q.toLowerCase());
    },

    activeMine() { return this.myReviews.filter((r) => !r.archivedBySubmitter); },
    archivedMine() { return this.myReviews.filter((r) => !!r.archivedBySubmitter); },
    activeMineCount() { return this.activeMine.length; },
    archivedMineCount() { return this.archivedMine.length; },
    mineBase() { return this.mineView === 'archive' ? this.archivedMine : this.activeMine; },
    filteredMineReviews() {
      if (this.mineFilterStatus === 'all') return this.mineBase;
      if (this.mineFilterStatus === 'in_progress') {
        return this.mineBase.filter((r) => ['pending_technical', 'fix_pending_technical', 'pending_visual', 'fix_pending_visual'].includes(r.status));
      }
      if (this.mineFilterStatus === 'rejected') {
        return this.mineBase.filter((r) => ['rejected_technical', 'rejected_visual'].includes(r.status));
      }
      if (this.mineFilterStatus === 'approved') {
        return this.mineBase.filter((r) => r.status === 'approved');
      }
      return this.mineBase;
    },
    mineStatusFilterItems() {
      return [
        { label: 'Alle',        value: 'all' },
        { label: 'In Prüfung',  value: 'in_progress' },
        { label: 'Abgelehnt',   value: 'rejected' },
        { label: 'Approved',    value: 'approved' },
      ];
    },
    currentMineFilterLabel() {
      return this.mineStatusFilterItems.find((i) => i.value === this.mineFilterStatus)?.label || 'Alle';
    },

    technicalPendingCount() { return this.technicalPendingReviews.length; },
    technicalReviewsForBucket() {
      if (this.technicalBucket === 'pending') return this.technicalPendingReviews;
      if (this.technicalBucket === 'rejected') return this.technicalRejectedReviews;
      return this.technicalApprovedReviews;
    },
    technicalEmptyTitle() {
      if (this.technicalBucket === 'pending') return 'Keine offenen technischen Reviews';
      if (this.technicalBucket === 'rejected') return 'Keine abgelehnten Reviews';
      return 'Noch nichts technisch durchgereicht';
    },

    visualPendingCount() { return this.visualPendingReviews.length; },
    visualReviewsForBucket() {
      if (this.visualBucket === 'pending') return this.visualPendingReviews;
      if (this.visualBucket === 'rejected') return this.visualRejectedReviews;
      return this.visualApprovedReviews;
    },
    visualEmptyTitle() {
      if (this.visualBucket === 'pending') return 'Keine offenen optischen Reviews';
      if (this.visualBucket === 'rejected') return 'Keine abgelehnten Reviews';
      return 'Noch nichts approved';
    },

    rejectDialogTitle() {
      if (!this.rejectTarget) return 'Review ablehnen';
      const s = this.rejectTarget.status;
      if (['pending_technical', 'fix_pending_technical'].includes(s)) return 'Technisch ablehnen';
      if (['pending_visual', 'fix_pending_visual'].includes(s)) return 'Optisch ablehnen';
      return 'Review ablehnen';
    },
    rejectDialogHint() {
      if (!this.rejectTarget) return '';
      const s = this.rejectTarget.status;
      if (['pending_technical', 'fix_pending_technical'].includes(s)) {
        return 'Einreicher muss die technischen Punkte fixen und erneut einreichen.';
      }
      if (['pending_visual', 'fix_pending_visual'].includes(s)) {
        return 'Einreicher muss die optischen Punkte fixen und erneut einreichen.';
      }
      return '';
    },
  },

  watch: {
    activeTab(val) {
      if (val === 'mine') this.loadMyReviews();
      if (val === 'technical') this.loadTechnicalReviews();
      if (val === 'visual') this.loadVisualReviews();
    },
    'form.shopDomain'(val) {
      this.form.ablyftExperiment = null;
      this.ablyftExperiments = [];
      if (val && val.ablyftProjectId) {
        this.loadAblyftExperiments(val.ablyftProjectId);
      }
    },
  },

  mounted() {
    this.loadShops();
    if (this.activeTab === 'mine') this.loadMyReviews();
    if (this.activeTab === 'technical') this.loadTechnicalReviews();
    if (this.activeTab === 'visual') this.loadVisualReviews();
    if (this.$user?.isTechnicalReviewer && this.activeTab !== 'technical') this.loadTechnicalPendingOnly();
    if (this.$user?.isVisualReviewer && this.activeTab !== 'visual') this.loadVisualPendingOnly();
    document.addEventListener('click', this.handleOutsideClick);
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
  },

  methods: {
    switchTab(tab) {
      this.activeTab = tab;
      const url = new URL(window.location);
      url.searchParams.set('tab', tab);
      window.history.replaceState({}, '', url);
    },

    refreshCurrentTab() {
      if (this.activeTab === 'mine') this.loadMyReviews();
      else if (this.activeTab === 'technical') this.loadTechnicalReviews();
      else if (this.activeTab === 'visual') this.loadVisualReviews();
    },

    handleOutsideClick(e) {
      if (this.shopMenuOpen) {
        const el = this.$refs.shopDropdown;
        if (el && !el.contains(e.target)) this.shopMenuOpen = false;
      }
      if (this.ablyftMenuOpen) {
        const el = this.$refs.ablyftDropdown;
        if (el && !el.contains(e.target)) this.ablyftMenuOpen = false;
      }
      if (this.statusFilterMenuOpen) {
        const el = this.$refs.statusFilterDropdown;
        if (el && !el.contains(e.target)) this.statusFilterMenuOpen = false;
      }
    },

    selectMineFilter(value) {
      this.mineFilterStatus = value;
      this.statusFilterMenuOpen = false;
    },

    // ---- Shop Combobox ----
    onShopFocus() {
      this.focusField = 'shop';
      this.shopMenuOpen = true;
    },
    selectShop(shop) {
      this.form.shopDomain = shop;
      this.shopSearch = shop.name;
      this.shopMenuOpen = false;
    },
    clearShop() {
      this.form.shopDomain = null;
      this.shopSearch = '';
      this.ablyftExperiments = [];
      this.form.ablyftExperiment = null;
    },
    async createShop() {
      const name = (this.shopSearch || '').trim();
      if (!name) return;
      try {
        const res = await axios.post('/api/reviews/shops/create', { name });
        const shop = res.data.data;
        if (!this.shops.find((s) => s.name === shop.name)) {
          this.shops.push(shop);
          this.shops.sort((a, b) => a.name.localeCompare(b.name));
        }
        this.selectShop(shop);
      } catch (e) {
        console.error('Fehler beim Anlegen des Shops', e);
      }
    },

    // ---- ABLyft Select ----
    selectAblyft(exp) {
      this.form.ablyftExperiment = exp;
      this.ablyftMenuOpen = false;
    },

    // ---- Data Loading ----
    async loadMyReviews() {
      this.loadingMyReviews = true;
      try {
        const res = await axios.get('/api/reviews/get', { params: { submittedBy: this.$user?.id } });
        this.myReviews = res.data.data || [];
      } catch (e) {
        console.error('Fehler beim Laden der eigenen Reviews', e);
      } finally {
        this.loadingMyReviews = false;
      }
    },

    async loadTechnicalReviews() {
      this.loadingTechnical = true;
      try {
        const [pending, rejected, approved] = await Promise.all([
          this.fetchStageBucket('technical', 'pending'),
          this.fetchStageBucket('technical', 'rejected'),
          this.fetchStageBucket('technical', 'approved'),
        ]);
        this.technicalPendingReviews = pending;
        this.technicalRejectedReviews = rejected;
        this.technicalApprovedReviews = approved;
      } catch (e) {
        console.error('Fehler beim Laden der technischen Reviews', e);
      } finally {
        this.loadingTechnical = false;
      }
    },

    async loadTechnicalPendingOnly() {
      try { this.technicalPendingReviews = await this.fetchStageBucket('technical', 'pending'); }
      catch (e) { console.error(e); }
    },

    async loadVisualReviews() {
      this.loadingVisual = true;
      try {
        const [pending, rejected, approved] = await Promise.all([
          this.fetchStageBucket('visual', 'pending'),
          this.fetchStageBucket('visual', 'rejected'),
          this.fetchStageBucket('visual', 'approved'),
        ]);
        this.visualPendingReviews = pending;
        this.visualRejectedReviews = rejected;
        this.visualApprovedReviews = approved;
      } catch (e) {
        console.error('Fehler beim Laden der optischen Reviews', e);
      } finally {
        this.loadingVisual = false;
      }
    },

    async loadVisualPendingOnly() {
      try { this.visualPendingReviews = await this.fetchStageBucket('visual', 'pending'); }
      catch (e) { console.error(e); }
    },

    async fetchStageBucket(stage, bucket) {
      const res = await axios.get('/api/reviews/get', {
        params: {
          stage, bucket,
          excludeOwn: bucket === 'pending' ? '1' : '0',
          requesterId: this.$user?.id || '',
        },
      });
      return res.data.data || [];
    },

    async loadAblyftExperiments(projectId) {
      this.loadingExperiments = true;
      try {
        const res = await axios.get('/api/reviews/ablyft/experiments', {
          params: { projectId, status: 'preview' },
        });
        this.ablyftExperiments = res.data.data || [];
      } catch (e) {
        console.error('Fehler beim Laden der ABLyft-Tests', e);
        this.ablyftExperiments = [];
      } finally {
        this.loadingExperiments = false;
      }
    },

    async loadShops() {
      this.loadingShops = true;
      try {
        const res = await axios.get('/api/reviews/shops');
        this.shops = res.data.data || [];
      } catch (e) {
        console.error('Fehler beim Laden der Shops', e);
      } finally {
        this.loadingShops = false;
      }
    },

    // ---- Formular ----
    async onScreenshotSelected(e) {
      const files = Array.from(e.target.files || []);
      e.target.value = '';
      await this.addScreenshots(files);
    },

    async onScreenshotDrop(e) {
      const files = Array.from(e.dataTransfer.files || []);
      await this.addScreenshots(files);
    },

    async addScreenshots(files) {
      const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/avif', 'image/gif', 'image/heic', 'image/heif'];
      for (const file of files) {
        if (this.screenshotFiles.length >= 3) break;
        if (!allowed.includes(file.type)) continue;
        try {
          const compressed = await this.compressImage(file);
          this.screenshotFiles.push(compressed);
          const reader = new FileReader();
          reader.onload = (ev) => this.screenshotPreviews.push(ev.target.result);
          reader.readAsDataURL(compressed);
        } catch (err) {
          console.error('Komprimierung fehlgeschlagen:', err);
        }
      }
    },

    sanitizeFilename(name) {
      if (!name) return 'screenshot';
      const dot = name.lastIndexOf('.');
      const base = dot > 0 ? name.substring(0, dot) : name;
      return base.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').substring(0, 80) || 'screenshot';
    },

    compressImage(file) {
      return new Promise((resolve, reject) => {
        const MAX_DIMENSION = 1920;
        const QUALITY = 0.82;
        const cleanName = this.sanitizeFilename(file.name) + '.jpg';

        if (file.type === 'image/gif' || file.type === 'image/heic' || file.type === 'image/heif') {
          const renamed = new File([file], cleanName.replace(/\.jpg$/, this.getExtFromMime(file.type)), { type: file.type });
          resolve(renamed);
          return;
        }

        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
          URL.revokeObjectURL(url);
          let { width, height } = img;
          if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
            if (width >= height) { height = Math.round(height * (MAX_DIMENSION / width)); width = MAX_DIMENSION; }
            else { width = Math.round(width * (MAX_DIMENSION / height)); height = MAX_DIMENSION; }
          }
          const canvas = document.createElement('canvas');
          canvas.width = width; canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob((blob) => {
            if (!blob) { reject(new Error('Canvas toBlob fehlgeschlagen')); return; }
            if (blob.size >= file.size) {
              const renamedOriginal = new File([file], this.sanitizeFilename(file.name) + this.getExtFromMime(file.type), { type: file.type });
              resolve(renamedOriginal);
              return;
            }
            resolve(new File([blob], cleanName, { type: 'image/jpeg' }));
          }, 'image/jpeg', QUALITY);
        };
        img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Bild konnte nicht geladen werden')); };
        img.src = url;
      });
    },

    getExtFromMime(mime) {
      const map = {
        'image/png': '.png', 'image/jpeg': '.jpg', 'image/jpg': '.jpg',
        'image/webp': '.webp', 'image/avif': '.avif',
        'image/gif': '.gif', 'image/heic': '.heic', 'image/heif': '.heif',
      };
      return map[mime] || '.jpg';
    },

    removeScreenshot(i) {
      this.screenshotFiles.splice(i, 1);
      this.screenshotPreviews.splice(i, 1);
    },

    async submitReview() {
      if (!this.form.ablyftExperiment || !this.form.testPage || !this.form.device || !this.form.ablyftChecked) {
        this.submitMessage = 'Bitte fülle alle Pflichtfelder aus.';
        this.submitSuccess = false;
        return;
      }
      this.submitting = true;
      this.submitMessage = '';
      try {
        const fd = new FormData();
        fd.append('shopDomain', this.form.shopDomain?.name || '');
        fd.append('ablyftExperimentId', this.form.ablyftExperiment?.id || '');
        fd.append('ablyftExperimentName', this.form.ablyftExperiment?.name || '');
        fd.append('testNumber', this.form.ablyftExperiment?.name || '');
        fd.append('testPage', this.form.testPage);
        fd.append('device', this.form.device);
        fd.append('ablyftChecked', this.form.ablyftChecked);
        fd.append('additionalInfo', this.form.additionalInfo || '');
        fd.append('deadline', this.form.deadline || '');
        fd.append('submittedBy', this.$user?.id || '');
        for (const file of this.screenshotFiles) fd.append('screenshots', file);

        await axios.post('/api/reviews/create', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        this.submitMessage = 'Review erfolgreich eingereicht!';
        this.submitSuccess = true;
        this.resetForm();
        this.switchTab('mine');
        await this.loadMyReviews();
      } catch (e) {
        this.submitMessage = e.response?.data?.message || 'Fehler beim Einreichen.';
        this.submitSuccess = false;
      } finally {
        this.submitting = false;
      }
    },

    resetForm() {
      this.form = { shopDomain: null, ablyftExperiment: null, testPage: '', device: 'Mobile', ablyftChecked: null, additionalInfo: '', deadline: '' };
      this.shopSearch = '';
      this.ablyftExperiments = [];
      this.screenshotFiles = [];
      this.screenshotPreviews = [];
    },

    // ---- Reviewer Actions ----
    async acceptReview(review) {
      this.acceptingId = review.id;
      try {
        await axios.post('/api/reviews/accept', { id: review.id, reviewedBy: this.$user?.id });
        await this.reloadStageForStatus(review.status);
      } catch (e) {
        console.error('Fehler beim Akzeptieren', e);
        alert(e.response?.data?.message || 'Fehler beim Akzeptieren');
      } finally {
        this.acceptingId = null;
      }
    },

    openRejectDialog(review) {
      this.rejectTarget = review;
      this.rejectForm = { reason: '', screenshotFile: null, screenshotPreview: null };
      this.rejectMessage = '';
      this.rejectDialog = true;
    },

    async onRejectScreenshotSelected(e) {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;
      try {
        const compressed = await this.compressImage(file);
        this.rejectForm.screenshotFile = compressed;
        const reader = new FileReader();
        reader.onload = (ev) => (this.rejectForm.screenshotPreview = ev.target.result);
        reader.readAsDataURL(compressed);
      } catch (err) {
        console.error('Komprimierung fehlgeschlagen:', err);
      }
    },

    async submitReject() {
      if (!this.rejectForm.reason.trim()) {
        this.rejectMessage = 'Bitte gib einen Ablehnungsgrund ein.';
        return;
      }
      this.rejecting = true;
      this.rejectMessage = '';
      try {
        const fd = new FormData();
        fd.append('id', this.rejectTarget.id);
        fd.append('rejectionReason', this.rejectForm.reason);
        fd.append('reviewedBy', this.$user?.id || '');
        if (this.rejectForm.screenshotFile) fd.append('rejectionScreenshot', this.rejectForm.screenshotFile);

        await axios.post('/api/reviews/reject', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        const originalStatus = this.rejectTarget.status;
        this.rejectDialog = false;
        await this.reloadStageForStatus(originalStatus);
      } catch (e) {
        this.rejectMessage = e.response?.data?.message || 'Fehler beim Ablehnen.';
      } finally {
        this.rejecting = false;
      }
    },

    async reloadStageForStatus(originalStatus) {
      if (['pending_technical', 'fix_pending_technical', 'rejected_technical'].includes(originalStatus)) {
        await this.loadTechnicalReviews();
      }
      if (['pending_visual', 'fix_pending_visual', 'rejected_visual'].includes(originalStatus)) {
        await Promise.all([this.loadVisualReviews(), this.loadTechnicalReviews()]);
      }
    },

    async resubmitReview(review) {
      this.resubmittingId = review.id;
      try {
        await axios.post('/api/reviews/resubmit', { id: review.id });
        await this.loadMyReviews();
      } catch (e) { console.error('Fehler beim erneuten Einreichen', e); }
      finally { this.resubmittingId = null; }
    },

    async archiveReview(review) {
      this.archivingId = review.id;
      try {
        await axios.post('/api/reviews/archive', { id: review.id, requesterId: this.$user?.id });
        await this.loadMyReviews();
      } catch (e) {
        console.error('Fehler beim Archivieren', e);
        alert(e.response?.data?.message || 'Fehler beim Archivieren');
      } finally { this.archivingId = null; }
    },

    async unarchiveReview(review) {
      this.archivingId = review.id;
      try {
        await axios.post('/api/reviews/unarchive', { id: review.id, requesterId: this.$user?.id });
        await this.loadMyReviews();
      } catch (e) { console.error('Fehler beim De-Archivieren', e); }
      finally { this.archivingId = null; }
    },

    openScreenshot(src) {
      this.lightboxSrc = src;
      this.lightboxDialog = true;
    },
  },
};
</script>

<style scoped>
.page-title { font-size: 26px; font-weight: 800; color: #0a1628; letter-spacing: -0.3px; }
.page-subtitle { font-size: 13px; color: #888; }

/* Buttons */
.icon-btn-outlined {
  display: inline-flex; align-items: center; justify-content: center;
  width: 40px; height: 40px;
  background: white; border: 1px solid #eceef2; border-radius: 10px;
  cursor: pointer; color: #0a1628; transition: border-color 0.15s;
}
.icon-btn-outlined:hover:not(:disabled) { border-color: #0a1628; }
.icon-btn-outlined:disabled { opacity: 0.5; cursor: not-allowed; }

/* Tab Bar */
.tab-bar { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.tab {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 8px 14px; background: white; border: 1px solid #eceef2;
  border-radius: 10px; font-size: 13px; font-weight: 600; color: #666;
  cursor: pointer; transition: all 0.15s; font-family: inherit;
}
.tab:hover:not(.tab-active) { border-color: #c5cbd3; color: #0a1628; }
.tab-active { background: #0a1628; color: white; border-color: #0a1628; }
.tab-count {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 20px; height: 20px; padding: 0 6px; border-radius: 10px;
  background: #eceef2; color: #0a1628; font-size: 11px; font-weight: 700;
}
.tab-active .tab-count { background: #eceef2; color: #0a1628; }
.tab-count-warning { background: #eceef2 !important; color: #0a1628 !important; }

/* Sub Toolbar (segments + filter) */
.sub-toolbar { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.seg-toggle {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 4px; background: white; border: 1px solid #eceef2; border-radius: 10px;
}
.seg-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 7px 12px; background: transparent; border: none; border-radius: 7px;
  font-size: 13px; font-weight: 600; color: #666; cursor: pointer;
  transition: all 0.15s; font-family: inherit;
}
.seg-btn:hover:not(.seg-active) { background: #f8f9fb; color: #0a1628; }
.seg-active { background: #0a1628; color: white; }
.seg-count {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 18px; height: 18px; padding: 0 5px; border-radius: 9px;
  background: #eceef2; color: #0a1628; font-size: 10px; font-weight: 700;
}
.seg-active .seg-count { background: #eceef2; color: #0a1628; }
.seg-count-warning { background: #eceef2 !important; color: #0a1628 !important; }

.filter-select {
  display: flex; align-items: center; gap: 10px;
  padding: 0 14px; height: 40px; background: white;
  border: 1px solid #eceef2; border-radius: 10px; flex: 1;
  min-width: 160px;
}
.filter-select .ii {
  flex: 1; border: none; outline: none; background: transparent;
  font-size: 13px; color: #0a1628; font-weight: 500;
  appearance: none; cursor: pointer;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path fill='%23888' d='M6 8L0 0h12z'/></svg>");
  background-repeat: no-repeat; background-position: right center;
  padding-right: 16px;
}

/* Card */
.card-modern { background: white; border: 1px solid #eceef2; border-radius: 16px; padding: 28px; }
.section-title { font-size: 18px; font-weight: 700; color: #0a1628; }

/* Fields */
.fg { display: flex; flex-direction: column; position: relative; }
.fl {
  font-size: 11px; font-weight: 600; color: #999;
  margin-bottom: 6px; letter-spacing: 0.3px;
}
.fl-hint { color: #bbb; font-weight: 400; text-transform: none; letter-spacing: 0; margin-left: 4px; }

.iw {
  display: flex; align-items: center; gap: 10px;
  min-height: 42px; padding: 0 14px;
  background: #f8f9fb; border: 1px solid #eceef2; border-radius: 10px;
  transition: all 0.15s;
}
.iw.iw-f { border-color: #0a1628; background: white; box-shadow: 0 0 0 3px rgba(10,22,40,0.04); }
.iw.iw-disabled { opacity: 0.6; cursor: not-allowed; background: #f0f2f5; }
.iw-trigger {
  cursor: pointer; font-family: inherit; text-align: left;
  width: 100%; background: white !important; border-color: #eceef2;
}
.iw-trigger:hover:not(:disabled):not(.iw-f) { border-color: #c5cbd3; }
.iw-ta { min-height: auto; padding: 10px 14px; align-items: flex-start; }

.ii {
  flex: 1; border: none; outline: none; background: transparent;
  font-size: 14px; color: #0a1628; font-weight: 500; min-width: 0;
  font-family: inherit; height: 40px;
}
.ii::placeholder { color: #bbb; font-weight: 400; }
.ii-ta { height: auto; resize: none; line-height: 1.5; padding: 0; }
.ii-val { flex: 1; font-size: 14px; color: #0a1628; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ii-placeholder { flex: 1; font-size: 14px; color: #bbb; font-weight: 400; }

.chev-open { transform: rotate(180deg); transition: transform 0.2s; }

/* Combobox Menu */
.combo-wrap { position: relative; }
.combo-menu {
  position: absolute; top: calc(100% + 6px); left: 0; right: 0;
  background: white; border: 1px solid #eceef2; border-radius: 10px;
  box-shadow: 0 12px 32px rgba(10,22,40,0.12);
  z-index: 50; max-height: 260px; overflow-y: auto;
  padding: 6px; animation: empFadeIn 0.12s ease;
}
@keyframes empFadeIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }

.combo-option {
  display: flex; align-items: center; gap: 10px; width: 100%;
  padding: 9px 10px; background: transparent; border: none;
  border-radius: 7px; cursor: pointer; transition: background 0.1s;
  text-align: left; font-family: inherit;
}
.combo-option:hover { background: #f8f9fb; }
.combo-option-create { color: #5B5BD6; font-weight: 600; }
.combo-option-create:hover { background: rgba(91,91,214,0.06); }
.combo-label { flex: 1; font-size: 14px; color: #0a1628; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.combo-option-create .combo-label { color: #5B5BD6; }
.combo-empty { padding: 12px 14px; font-size: 13px; color: #999; text-align: center; }

.menu-spacer { height: 268px; animation: empFadeIn 0.15s ease; }

.ablyft-link { margin-top: 6px; }
.ablyft-link a {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 12px; color: #5B5BD6; text-decoration: none; font-weight: 500;
}
.ablyft-link a:hover { text-decoration: underline; }

/* Device toggle */
.device-toggle {
  display: flex; gap: 4px; padding: 4px;
  background: #f8f9fb; border: 1px solid #eceef2; border-radius: 10px;
}
.device-btn {
  flex: 1; display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  padding: 10px 14px; background: transparent; border: none; border-radius: 7px;
  font-size: 13px; font-weight: 600; color: #666; cursor: pointer;
  transition: all 0.15s; font-family: inherit;
}
.device-btn:hover:not(.device-active) { background: white; color: #0a1628; }
.device-active { background: white; color: #0a1628; box-shadow: 0 1px 3px rgba(10,22,40,0.08); }

/* Dropzone */
.dropzone {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 32px 16px; background: #fafbfc; border: 1px dashed #c5cbd3; border-radius: 12px;
  cursor: pointer; transition: all 0.15s; text-align: center;
}
.dropzone:hover { border-color: #0a1628; background: white; }
.dz-title { font-size: 13px; font-weight: 600; color: #0a1628; margin-bottom: 2px; }
.dz-sub { font-size: 11px; color: #888; }

.dropzone-small {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 14px; background: #fafbfc; border: 1px dashed #c5cbd3; border-radius: 10px;
  font-size: 13px; font-weight: 500; color: #555; cursor: pointer;
  width: 100%; transition: all 0.15s; font-family: inherit;
}
.dropzone-small:hover { border-color: #0a1628; color: #0a1628; background: white; }

.screenshot-previews { display: grid; grid-template-columns: repeat(auto-fill, minmax(120px, 1fr)); gap: 8px; }
.preview-thumb {
  position: relative; border: 1px solid #eceef2; border-radius: 10px;
  overflow: hidden; background: #f8f9fb;
}
.preview-remove {
  position: absolute; top: 6px; right: 6px;
  width: 22px; height: 22px; border-radius: 50%; border: none;
  background: rgba(10,22,40,0.75); color: white; cursor: pointer;
  display: flex; align-items: center; justify-content: center; transition: background 0.15s;
}
.preview-remove:hover { background: #D32F2F; }

/* Radio option */
.radio-option {
  display: flex; align-items: center; gap: 12px;
  padding: 14px 18px; background: white; border: 1px solid #eceef2;
  border-radius: 10px; cursor: pointer; transition: all 0.15s;
}
.radio-option:hover { border-color: #c5cbd3; }
.radio-active { border-color: #0a1628; background: #fafbfc; box-shadow: 0 0 0 3px rgba(10,22,40,0.04); }
.radio-dot {
  width: 18px; height: 18px; border-radius: 50%;
  border: 2px solid #c5cbd3; transition: all 0.15s; flex-shrink: 0;
}
.radio-dot-on {
  border-color: #0a1628;
  background: radial-gradient(circle, #0a1628 0, #0a1628 5px, transparent 5px);
}
.radio-label { font-size: 14px; color: #0a1628; font-weight: 500; }

/* Submit button */
.submit-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  width: 100%; padding: 14px 20px;
  background: #2E7D32; color: white; border: none; border-radius: 12px;
  font-size: 14px; font-weight: 700; cursor: pointer;
  transition: background 0.15s; font-family: inherit;
}
.submit-btn:hover:not(:disabled) { background: #1B5E20; }
.submit-btn:disabled { opacity: 0.6; cursor: not-allowed; }

.alert-success {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 14px; background: rgba(46,125,50,0.08); color: #2E7D32;
  border-radius: 10px; font-size: 13px; font-weight: 500;
}
.alert-error {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 14px; background: rgba(211,47,47,0.08); color: #D32F2F;
  border-radius: 10px; font-size: 13px; font-weight: 500;
}

/* Dialog (floowseo pattern) */
.dialog-backdrop { position: fixed; inset: 0; background: rgba(10,22,40,0.5); z-index: 2000; animation: fadeBg 0.2s ease; }
@keyframes fadeBg { from { opacity: 0; } to { opacity: 1; } }

.modern-dialog {
  background: white !important;
  box-shadow: 0 24px 64px rgba(10,22,40,0.18) !important;
  z-index: 2001 !important; position: relative;
  animation: slideUpDlg 0.3s cubic-bezier(0.16,1,0.3,1);
  overflow: visible !important;
}
@keyframes slideUpDlg { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }

.close-btn {
  position: absolute; top: 16px; right: 16px; z-index: 10;
  width: 32px; height: 32px; display: flex; align-items: center; justify-content: center;
  background: transparent; border: none; border-radius: 8px; cursor: pointer; color: #888;
  transition: all 0.2s;
}
.close-btn:hover { background: #f0f2f5; color: #0a1628; transform: rotate(90deg); }
.close-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.dh { display: flex; align-items: center; gap: 14px; padding: 28px 28px 0; }
.dh-icon {
  width: 44px; height: 44px; border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  background: rgba(10,22,40,0.06); flex-shrink: 0;
}
.dh-title { font-size: 18px; font-weight: 700; color: #0a1628; }
.dh-sub { font-size: 12px; color: #999; margin-top: 2px; }

.db { padding: 24px 28px; max-height: 70vh; overflow-y: auto; }
.dff {
  display: flex; align-items: center; justify-content: flex-end;
  gap: 10px; padding: 4px 28px 24px;
}

.bo {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 10px 18px; border: 1px solid #eceef2; border-radius: 10px;
  background: white; font-size: 13px; font-weight: 600; color: #555;
  cursor: pointer; transition: border-color 0.15s; font-family: inherit;
}
.bo:hover:not(:disabled) { border-color: #0a1628; }
.bo:disabled { opacity: 0.5; cursor: not-allowed; }

.bp {
  display: inline-flex; align-items: center; gap: 6px; min-width: 120px;
  padding: 10px 20px; background: #0a1628; color: white;
  border: none; border-radius: 10px; font-size: 13px; font-weight: 700;
  cursor: pointer; transition: background 0.15s; justify-content: center;
  font-family: inherit;
}
.bp:hover:not(:disabled) { background: #1d3457; }
.bp:disabled { opacity: 0.5; cursor: not-allowed; }
.bp-danger { background: #D32F2F; }
.bp-danger:hover:not(:disabled) { background: #B71C1C; }

/* Lightbox */
.lightbox-card {
  background: #0f172a !important; position: relative;
  padding: 0 !important; overflow: hidden !important;
}
.lightbox-close {
  position: absolute; top: 12px; right: 12px; z-index: 10;
  width: 36px; height: 36px; display: flex; align-items: center; justify-content: center;
  background: rgba(255,255,255,0.15); border: none; border-radius: 8px;
  cursor: pointer; backdrop-filter: blur(8px); transition: background 0.15s;
}
.lightbox-close:hover { background: rgba(255,255,255,0.25); }

@media (max-width: 600px) {
  .sub-toolbar { flex-direction: column; align-items: stretch; }
  .filter-select { width: 100%; max-width: none !important; }
  .device-toggle { flex-direction: column; }
}
</style>
